import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../../U3141';
import { U3141_DOCUMENT_UPLOAD_MAX_FILES } from '../../../U3141Service';
import { u3141AppendDocumentUploadFilesToQueue } from '../../../tools/u3141DocumentUploadFileQueue';

export interface U3141DocumentPdfFilesQueueUploadProps {
  label?: string;
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
  formResetKey?: number;
  /** Namespace i18n (ex. `u3141`, `u3142`). */
  i18nNs?: string;
  /** Zone DnD + input multiple (dialogue ajout). */
  enableDragDrop?: boolean;
  /** Affichage zone DnD et infobulle selon mono / multi fichier. */
  allowsMultipleFiles?: boolean;
}

export const U3141DocumentPdfFilesQueueUpload: React.FC<U3141DocumentPdfFilesQueueUploadProps> = ({
  label,
  files,
  onChange,
  maxFiles = U3141_DOCUMENT_UPLOAD_MAX_FILES,
  disabled = false,
  formResetKey = 0,
  i18nNs = NS,
  enableDragDrop = true,
  allowsMultipleFiles = true,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [inlineError, setInlineError] = React.useState<string | null>(null);

  const applyIncoming = useCallback(
    (incoming: File[]) => {
      setInlineError(null);
      const merged = u3141AppendDocumentUploadFilesToQueue(files, incoming);
      if (merged.rejectedOverMax) {
        setInlineError(t('msgDocumentUploadQueueFull', { ns: i18nNs, max: maxFiles }));
        return;
      }
      if (merged.rejectedNonPdf.length > 0) {
        setInlineError(t('msgDocumentUploadInvalidPdf', { ns: i18nNs }));
      }
      if (merged.duplicateNames.length > 0) {
        toast.info(t('msgDocumentUploadDuplicatesIgnored', { ns: i18nNs, count: merged.duplicateNames.length }));
      }
      onChange(merged.files.slice(0, maxFiles));
    },
    [files, i18nNs, maxFiles, onChange, t, toast],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const snapshot = e.target.files ? Array.from(e.target.files) : [];
    if (inputRef.current) inputRef.current.value = '';
    if (snapshot.length > 0) {
      applyIncoming(snapshot);
    }
  };

  const handleRemoveFile = (fileName: string) => {
    setInlineError(null);
    onChange(files.filter((f) => f.name !== fileName));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      applyIncoming(dropped);
    }
  };

  const inputId = 'u3141-document-files-queue';

  return (
    <div>
      {label ? (
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {inlineError ? (
        <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {inlineError}
        </div>
      ) : null}
      <input
        id={inputId}
        ref={inputRef}
        key={formResetKey}
        type="file"
        accept=".pdf,application/pdf"
        multiple={maxFiles > 1}
        disabled={disabled}
        onChange={handleFileChange}
        className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-sm file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
      />
      {enableDragDrop ? (
        <div
          role="button"
          tabIndex={0}
          data-testid="u3141-document-upload-drop-zone"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`mt-2 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors ${
            isDragActive
              ? 'border-green-500 bg-green-50 text-green-800'
              : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400'
          } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        >
          {allowsMultipleFiles
            ? t('msgDocumentUploadDropHintMulti', { ns: i18nNs })
            : t('msgDocumentUploadDropHintSingle', { ns: i18nNs })}
        </div>
      ) : null}
      <p className="mt-1 text-xs text-gray-500">
        {allowsMultipleFiles
          ? t('msgDocumentUploadMaxSizeAndCountMulti', { ns: i18nNs, max: maxFiles })
          : t('msgDocumentUploadMaxSizeSingle', { ns: i18nNs })}
      </p>
      {files.length > 0 ? (
        <div className="mt-2 max-h-52 overflow-y-auto rounded border border-gray-100 bg-gray-50 p-2">
          <p className="mb-1 text-xs font-medium text-gray-600">
            {t('lblDocumentUploadSelectedCount', { ns: i18nNs, count: files.length })}
          </p>
          <ul className="space-y-1 text-sm text-gray-700" data-testid="u3141-document-upload-file-queue">
            {files.map((f) => (
              <li key={f.name} className="flex items-center justify-between gap-2 rounded px-1 py-0.5">
                <span className="min-w-0 flex-1 truncate" title={f.name}>
                  {f.name}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(f.name)}
                  disabled={disabled}
                  className="shrink-0 text-sm text-red-600 hover:underline disabled:opacity-60"
                >
                  ({t('btnRemoveDocumentFromQueue', { ns: i18nNs })})
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
